import { NetworkStatus, useQuery } from "@apollo/client";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  CircularProgress,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { Dialog } from "components/Dialog";
import { gql } from "generated-graphql";
import { useDebounce } from "hooks/useDebounce";
import { useTenant } from "hooks/useTenant";
import { useCallback, useEffect, useMemo, useRef, useState, type UIEvent } from "react";
import { TextInput } from "routes/Customer/Tasks/TextInput";

const RECENTS_PAGE_SIZE = 10;
const SEARCH_RESULTS_PAGE_SIZE = 10;
const EMPTY_THREAD_TITLE = "Untitled thread";
const SEARCH_DEBOUNCE_MS = 300;
const SCROLL_BUFFER_PX = 24;

const GET_SCOUT_CONVERSATIONS = gql(`
  query GetScoutConversations($tenantId: ID!, $search: String, $page: Int, $pageSize: Int) {
    getScoutConversations(tenantId: $tenantId, search: $search, page: $page, pageSize: $pageSize) {
      count
      items {
        id
        title
        updatedAt
        status
      }
    }
  }
`);

type ScoutThreadListItem = {
  id: string;
  title: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSelectThread: (thread: ScoutThreadListItem) => void;
};

export function SearchThreadsDialog({ open, onClose, onSelectThread }: Props) {
  const {
    searchInput,
    setSearchInput,
    heading,
    items,
    isInitialLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    emptyMessage,
    errorMessage,
  } = useScoutThreadsSearch(open);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      noPrimaryAction
      DialogContentProps={{ sx: { paddingBottom: 2 } }}
      title={
        <TextInput
          placeholder="Search threads..."
          fullWidth
          autoFocus
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          startAdornment={
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          }
        />
      }
    >
      <ThreadListSection
        heading={heading}
        items={items}
        loading={isInitialLoading}
        hasMore={hasMore}
        loadingMore={isLoadingMore}
        emptyMessage={emptyMessage}
        errorMessage={errorMessage}
        onLoadMore={loadMore}
        onSelectItem={onSelectThread}
      />
    </Dialog>
  );
}

function useScoutThreadsSearch(open: boolean) {
  const { tenantId } = useTenant();
  const [searchInput, setSearchInput] = useState("");
  const trimmedSearch = searchInput.trim();
  const [debouncedSearch] = useDebounce(trimmedSearch, SEARCH_DEBOUNCE_MS);
  const isSearchMode = trimmedSearch.length > 0;
  const search = debouncedSearch.length > 0 ? debouncedSearch : undefined;
  const pageSize = search ? SEARCH_RESULTS_PAGE_SIZE : RECENTS_PAGE_SIZE;
  const nextPageRef = useRef(0);
  // Increments whenever the active query context resets so an older fetchMore
  // completion cannot clear loading state or rewind pagination for a newer search.
  const fetchMoreRequestIdRef = useRef(0);
  const isFetchingMoreRef = useRef(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  useEffect(
    function resetPaginationState() {
      nextPageRef.current = 0;
      fetchMoreRequestIdRef.current += 1;
      isFetchingMoreRef.current = false;
      setIsLoadingMore(false);
    },
    [open, pageSize, search]
  );

  const { data, error, loading, fetchMore, networkStatus } = useQuery(GET_SCOUT_CONVERSATIONS, {
    skip: !open || !tenantId,
    // Always refetch page 0 when the dialog opens or the debounced search changes
    // so recents/search results reflect the latest server ordering.
    fetchPolicy: "network-only",
    // Re-render on fetchMore state transitions so we can keep existing rows visible
    // while showing the bottom loading affordance for infinite scroll.
    notifyOnNetworkStatusChange: true,
    variables: tenantId
      ? {
          tenantId,
          search,
          page: 0,
          pageSize,
        }
      : undefined,
  });

  const items = useMemo(() => {
    return (data?.getScoutConversations.items ?? []).map((item) => ({
      id: item.id,
      title: item.title?.trim() ? item.title : EMPTY_THREAD_TITLE,
    }));
  }, [data]);

  const totalCount = data?.getScoutConversations.count ?? 0;
  const hasMore = items.length < totalCount;
  const isInitialLoading =
    loading && items.length === 0 && networkStatus !== NetworkStatus.fetchMore;

  const loadMore = useCallback(async () => {
    if (!open || !tenantId || !hasMore || isFetchingMoreRef.current) {
      return;
    }

    const requestId = fetchMoreRequestIdRef.current;
    const nextPage = nextPageRef.current + 1;

    nextPageRef.current = nextPage;
    isFetchingMoreRef.current = true;
    setIsLoadingMore(true);

    try {
      await fetchMore({
        variables: {
          tenantId,
          search,
          page: nextPage,
          pageSize,
        },
      });
    } catch {
      if (fetchMoreRequestIdRef.current === requestId) {
        nextPageRef.current = nextPage - 1;
      }
    } finally {
      if (fetchMoreRequestIdRef.current === requestId) {
        isFetchingMoreRef.current = false;
        setIsLoadingMore(false);
      }
    }
  }, [fetchMore, hasMore, open, pageSize, search, tenantId]);

  return {
    searchInput,
    setSearchInput,
    heading: isSearchMode ? "Search results" : "Recents",
    items,
    isInitialLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    emptyMessage: isSearchMode ? "No threads found." : "No recent threads.",
    errorMessage: error ? "Unable to load threads." : undefined,
  };
}

function ThreadListSection({
  heading,
  items,
  loading,
  hasMore,
  loadingMore,
  emptyMessage,
  errorMessage,
  onLoadMore,
  onSelectItem,
}: {
  heading: string;
  items: ScoutThreadListItem[];
  loading?: boolean;
  hasMore: boolean;
  loadingMore: boolean;
  emptyMessage: string;
  errorMessage?: string;
  onLoadMore: () => Promise<void> | void;
  onSelectItem: (item: ScoutThreadListItem) => void;
}) {
  const theme = useTheme();
  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      if (loading || loadingMore || !hasMore) {
        return;
      }

      const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
      const isNearBottom = scrollHeight <= scrollTop + clientHeight + SCROLL_BUFFER_PX;

      if (isNearBottom) {
        void onLoadMore();
      }
    },
    [hasMore, loading, loadingMore, onLoadMore]
  );

  return (
    <Stack
      spacing={1}
      paddingBottom={theme.spacing(1)}
      sx={{ maxHeight: 200, overflow: "auto" }}
      data-testid="thread-list-scroll-container"
      onScroll={handleScroll}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="caption">{heading}</Typography>
      </Stack>
      <List disablePadding>
        {loading ? (
          Array.from({ length: 3 }, (_, i) => (
            <ListItemButton key={i}>
              <Skeleton
                data-testid="thread-list-skeleton"
                variant="text"
                width="100%"
                height={32}
              />
            </ListItemButton>
          ))
        ) : errorMessage ? (
          <Alert severity="error">{errorMessage}</Alert>
        ) : items.length === 0 ? (
          <ListItemButton disabled>
            <ListItemText primary={emptyMessage} />
          </ListItemButton>
        ) : (
          <>
            {items.map((item) => (
              <ListItemButton
                key={item.id}
                onClick={() => onSelectItem(item)}
                sx={{
                  width: "100%",
                  borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                  "&:last-child": {
                    borderBottom: "none",
                  },
                }}
              >
                <ListItemText primary={item.title} />
              </ListItemButton>
            ))}
            {loadingMore && (
              <ListItemButton disabled>
                <Stack width="100%" direction="row" justifyContent="center" paddingY={0.5}>
                  <CircularProgress size={18} />
                </Stack>
              </ListItemButton>
            )}
          </>
        )}
      </List>
    </Stack>
  );
}
